import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  STATUS_LABEL,
  formatDate,
  getMedicinesText,
  getStatus,
  getSymptomsText,
} from '../../../utils/medicalHistory';

const STATUS_TONE = {
  open: 'bg-red-100 text-red-700',
  under_treatment: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-green-200 text-green-900',
};
const FOLLOW_UP_STATUS = { active: 'Active', inactive: 'Inactive', completed: 'Completed' };

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs font-extrabold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="font-bold text-slate-800">{children}</p>
    </div>
  );
}

const visitTitle = (visit) => [visit.doctorName, visit.hospitalName].filter(Boolean).join(' - ') || 'Doctor visit';
const followUpTitle = (followUp) => [followUp.doctor, followUp.hospital].filter(Boolean).join(' - ');

/**
 * A read-only look at one check-in: what was wrong, the medicine, and any doctor
 * visits and follow-ups. Escape, the backdrop or the X closes it; the parent
 * puts focus back on the button that opened it.
 */
export default function MedicalCheckInModal({ record, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const status = getStatus(record);
  const visits = (record.doctorVisits || []).filter(Boolean);
  const followUps = (record.followUps || []).filter(Boolean);
  const hasTemperature = record.temperature !== undefined && record.temperature !== null && record.temperature !== '';

  return createPortal(
    <div
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="medical-checkin-title"
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 id="medical-checkin-title" className="text-2xl font-extrabold text-slate-900">
              Check-up on {formatDate(record.date)}
            </h2>
            <span className={`mt-2 inline-block rounded-lg px-3 py-1 text-sm font-bold ${STATUS_TONE[status]}`}>
              {STATUS_LABEL[status]}
            </span>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="mb-5 grid grid-cols-1 gap-4 rounded-xl bg-sky-50 p-4 sm:grid-cols-2">
          <Field label="Symptoms">{getSymptomsText(record)}</Field>
          <Field label="Medicines">{getMedicinesText(record)}</Field>
          {hasTemperature && <Field label="Temperature">{`${record.temperature}°C`}</Field>}
        </div>

        {visits.length > 0 && (
          <section className="mb-5" aria-label="Doctor visits">
            <h3 className="mb-2 text-lg font-extrabold text-slate-900">Doctor visits</h3>
            <ul className="flex flex-col gap-3">
              {visits.map((visit, index) => (
                <li key={visit._id || index} className="rounded-xl border border-gray-100 p-3">
                  <p className="font-extrabold text-slate-800">{visitTitle(visit)}</p>
                  {visit.visitDate && <p className="text-sm font-semibold text-gray-500">{formatDate(visit.visitDate)}</p>}
                  {visit.testDetails && (
                    <p className="mt-1 text-sm font-semibold text-slate-700">
                      <span className="text-gray-500">Tests: </span>
                      {visit.testDetails}
                    </p>
                  )}
                  {visit.conclusion && (
                    <p className="mt-1 text-sm font-semibold text-slate-700">
                      <span className="text-gray-500">What the doctor said: </span>
                      {visit.conclusion}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {followUps.length > 0 && (
          <section className="mb-2" aria-label="Follow-ups">
            <h3 className="mb-2 text-lg font-extrabold text-slate-900">Follow-ups</h3>
            <ul className="flex flex-col gap-3">
              {followUps.map((followUp, index) => (
                <li key={followUp._id || index} className="rounded-xl border border-gray-100 p-3">
                  <p className="font-extrabold text-slate-800">
                    {followUp.followUpDate ? `Follow up on ${formatDate(followUp.followUpDate)}` : 'Follow up'}
                  </p>
                  {followUpTitle(followUp) && (
                    <p className="text-sm font-semibold text-gray-500">{followUpTitle(followUp)}</p>
                  )}
                  {FOLLOW_UP_STATUS[followUp.status] && (
                    <p className="mt-1 text-sm font-semibold text-slate-700">
                      <span className="text-gray-500">Status: </span>
                      {FOLLOW_UP_STATUS[followUp.status]}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {visits.length === 0 && followUps.length === 0 && (
          <p className="text-sm font-semibold text-gray-500">No doctor visit or follow-up was needed for this check-up.</p>
        )}
      </div>
    </div>,
    document.body
  );
}
